import { useState, useEffect, useRef } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { customerVerifyPhone, customerSendPin, customerVerifyPin, getCustomerDocuments, uploadCustomerDocument } from '../lib/api';
import { toast } from 'sonner';
import translations from '../lib/translations';
import {
  Phone,
  Shield,
  Upload,
  Check,
  X,
  Clock,
  FileText,
  AlertCircle,
  Loader2,
  Camera,
  Image,
  ChevronRight,
  CheckCircle,
  XCircle,
  RefreshCw,
  HelpCircle,
  Globe,
} from 'lucide-react';

// Language Toggle Component
const LanguageToggle = ({ lang, setLang }) => (
  <div className="flex items-center gap-1 bg-slate-100 rounded-full p-0.5" data-testid="language-toggle">
    <button
      onClick={() => setLang('en')}
      className={`px-3 py-1 text-xs font-semibold rounded-full transition-colors ${lang === 'en' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
      data-testid="lang-en"
    >
      EN
    </button>
    <button
      onClick={() => setLang('es')}
      className={`px-3 py-1 text-xs font-semibold rounded-full transition-colors ${lang === 'es' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
      data-testid="lang-es"
    >
      ES
    </button>
  </div>
);

// Phone Entry Screen
const PhoneEntryScreen = ({ onSuccess, lang, setLang }) => {
  const t = translations[lang];
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const [deals, setDeals] = useState([]);
  const [error, setError] = useState('');

  const formatPhoneDisplay = (value) => {
    const digits = value.replace(/\D/g, '');
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  };

  const handlePhoneChange = (e) => {
    const formatted = formatPhoneDisplay(e.target.value);
    setPhone(formatted);
    setError('');
  };

  const handleVerify = async () => {
    const digits = phone.replace(/\D/g, '');
    if (digits.length < 10) {
      setError(t.phoneError);
      return;
    }

    setLoading(true);
    try {
      const res = await customerVerifyPhone(digits);
      if (res.data.success) {
        if (res.data.deals_count > 1) {
          setDeals(res.data.deals);
        } else {
          onSuccess(digits, res.data.deals[0]?.id);
        }
      } else {
        const msg = res.data.message;
        setError(lang === 'es' && msg?.includes('No active deals')
          ? 'No se encontraron transacciones activas para este numero de telefono. Comuniquese con su concesionario.'
          : msg);
      }
    } catch (err) {
      setError(err.response?.data?.detail || (lang === 'es' ? 'Ocurrio un error' : 'An error occurred'));
    } finally {
      setLoading(false);
    }
  };

  const handleSelectDeal = (dealId) => {
    const digits = phone.replace(/\D/g, '');
    onSuccess(digits, dealId);
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-white border border-slate-200 p-8">
          <div className="flex justify-end mb-4">
            <LanguageToggle lang={lang} setLang={setLang} />
          </div>
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-slate-900 flex items-center justify-center mx-auto mb-4">
              <Phone className="text-white" size={32} />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 uppercase tracking-tight">{t.docUpload}</h1>
            <p className="text-slate-500 mt-2">{t.enterPhone}</p>
          </div>

          {deals.length === 0 ? (
            <>
              <div className="mb-4">
                <label className="block text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">
                  {t.phoneNumber}
                </label>
                <Input
                  type="tel"
                  value={phone}
                  onChange={handlePhoneChange}
                  placeholder={t.phonePlaceholder}
                  className="text-lg text-center font-mono"
                  maxLength={14}
                  data-testid="phone-input"
                />
              </div>

              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 text-sm flex items-center gap-2">
                  <AlertCircle size={16} />
                  {error}
                </div>
              )}

              <Button
                onClick={handleVerify}
                disabled={loading || phone.replace(/\D/g, '').length < 10}
                className="w-full"
                data-testid="verify-phone-btn"
              >
                {loading ? (
                  <><Loader2 size={18} className="mr-2 animate-spin" /> {t.verifying}</>
                ) : (
                  <><ChevronRight size={18} className="mr-2" /> {t.sendCode}</>
                )}
              </Button>
            </>
          ) : (
            <>
              <p className="text-sm text-slate-600 mb-4">{t.multipleDeals}</p>
              <div className="space-y-2">
                {deals.map((deal) => (
                  <button
                    key={deal.id}
                    onClick={() => handleSelectDeal(deal.id)}
                    className="w-full p-4 border border-slate-200 text-left hover:border-slate-400 hover:bg-slate-50 transition-colors"
                    data-testid={`select-deal-${deal.id}`}
                  >
                    <p className="font-medium">{deal.customer_name_masked}</p>
                    <p className="text-sm text-slate-500">{deal.vehicle_info} • {deal.lender}</p>
                    <p className="text-xs text-slate-400">ID: {deal.deal_id_masked}</p>
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        <p className="text-center text-sm text-slate-400 mt-4">
          {t.needHelp}
        </p>
      </div>
    </div>
  );
};

// PIN Verification Screen
const PinVerificationScreen = ({ phone, dealId, onSuccess, onBack, lang, setLang }) => {
  const t = translations[lang];
  const [pin, setPin] = useState(['', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [countdown, setCountdown] = useState(0);
  const [testPin, setTestPin] = useState(null);
  const inputRefs = [useRef(), useRef(), useRef(), useRef()];

  useEffect(() => {
    sendPin();
  }, []);

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const sendPin = async () => {
    setSending(true);
    try {
      const res = await customerSendPin(phone, dealId);
      if (res.data.success) {
        toast.success(t.codeSent);
        setCountdown(60);
        if (res.data.pin_for_testing) {
          setTestPin(res.data.pin_for_testing);
        }
      } else {
        if (res.data.wait_seconds) {
          setCountdown(res.data.wait_seconds);
        }
        setError(res.data.message);
      }
    } catch (err) {
      setError(err.response?.data?.detail || t.verificationFailed);
    } finally {
      setSending(false);
    }
  };

  const handlePinChange = (index, value) => {
    if (value.length > 1) value = value[0];
    if (value && !/^\d$/.test(value)) return;

    const newPin = [...pin];
    newPin[index] = value;
    setPin(newPin);
    setError('');

    if (value && index < 3) {
      inputRefs[index + 1].current?.focus();
    }

    if (value && index === 3) {
      const fullPin = newPin.join('');
      if (fullPin.length === 4) {
        verifyPin(fullPin);
      }
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !pin[index] && index > 0) {
      inputRefs[index - 1].current?.focus();
    }
  };

  const verifyPin = async (pinCode) => {
    setLoading(true);
    try {
      const res = await customerVerifyPin(phone, pinCode, dealId);
      if (res.data.success) {
        toast.success(lang === 'es' ? 'Telefono verificado!' : 'Phone verified!');
        onSuccess(res.data.session_token, res.data.deal_id, res.data.customer_name);
      } else {
        setError(res.data.message);
        setPin(['', '', '', '']);
        inputRefs[0].current?.focus();
      }
    } catch (err) {
      setError(err.response?.data?.detail || t.verificationFailed);
      setPin(['', '', '', '']);
    } finally {
      setLoading(false);
    }
  };

  const maskedPhone = `***-***-${phone.slice(-4)}`;

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-white border border-slate-200 p-8">
          <div className="flex justify-end mb-4">
            <LanguageToggle lang={lang} setLang={setLang} />
          </div>
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-emerald-600 flex items-center justify-center mx-auto mb-4">
              <Shield className="text-white" size={32} />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 uppercase tracking-tight">{t.verifyPhone}</h1>
            <p className="text-slate-500 mt-2">{t.enterCode} {maskedPhone}</p>
          </div>

          <div className="flex justify-center gap-3 mb-6">
            {pin.map((digit, index) => (
              <input
                key={index}
                ref={inputRefs[index]}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={(e) => handlePinChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                className="w-14 h-14 text-center text-2xl font-mono border-2 border-slate-300 focus:border-slate-900 focus:outline-none"
                disabled={loading}
                data-testid={`pin-input-${index}`}
              />
            ))}
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 text-sm flex items-center gap-2">
              <AlertCircle size={16} />
              {error}
            </div>
          )}

          {testPin && (
            <div className="mb-4 p-3 bg-amber-50 border border-amber-200 text-amber-700 text-sm">
              <strong>{t.testPin}</strong> {testPin}
            </div>
          )}

          {loading && (
            <div className="flex items-center justify-center gap-2 text-slate-500 mb-4">
              <Loader2 className="animate-spin" size={18} />
              {t.verifying}
            </div>
          )}

          <div className="flex items-center justify-between">
            <button
              onClick={onBack}
              className="text-sm text-slate-500 hover:text-slate-700"
            >
              &larr; {t.changePhone}
            </button>
            <button
              onClick={sendPin}
              disabled={sending || countdown > 0}
              className="text-sm text-slate-900 hover:text-slate-700 disabled:text-slate-400 flex items-center gap-1"
            >
              <RefreshCw size={14} />
              {countdown > 0 ? `${t.resendIn} ${countdown}s` : t.resendCode}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Document Upload Dashboard
const DocumentUploadDashboard = ({ sessionToken, dealId, customerName, lang, setLang }) => {
  const t = translations[lang];
  const [documents, setDocuments] = useState([]);
  const [dealInfo, setDealInfo] = useState({});
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(null);
  const [sessionExpires, setSessionExpires] = useState(null);
  const fileInputRefs = {};

  useEffect(() => {
    loadDocuments();
  }, []);

  const loadDocuments = async () => {
    try {
      const res = await getCustomerDocuments(sessionToken);
      setDocuments(res.data.documents || []);
      setDealInfo({
        customerName: res.data.customer_name,
        vehicleInfo: res.data.vehicle_info,
        lender: res.data.lender
      });
      setSessionExpires(res.data.session_expires_at);
    } catch (err) {
      toast.error(t.loadError);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = async (documentType, file) => {
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf', 'image/heic', 'image/heif'];
    const maxSize = 20 * 1024 * 1024;

    if (!allowedTypes.includes(file.type) && !file.name.match(/\.(jpg|jpeg|png|pdf|heic|heif)$/i)) {
      toast.error(t.fileTypeError);
      return;
    }

    if (file.size > maxSize) {
      toast.error(t.fileSizeError);
      return;
    }

    setUploading(documentType);
    try {
      await uploadCustomerDocument(documentType, file, sessionToken);
      toast.success(`${documentType} ${t.uploadedSuccess}`);
      loadDocuments();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(null);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'verified': return <CheckCircle className="text-emerald-600" size={20} />;
      case 'uploaded': return <Clock className="text-amber-600" size={20} />;
      case 'rejected': return <XCircle className="text-red-600" size={20} />;
      default: return <AlertCircle className="text-slate-400" size={20} />;
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'verified': return t.verified;
      case 'uploaded': return t.pendingReviewStatus;
      case 'rejected': return t.rejected;
      default: return t.missing;
    }
  };

  const getStatusClass = (status) => {
    switch (status) {
      case 'verified': return 'bg-emerald-100 text-emerald-700';
      case 'uploaded': return 'bg-amber-100 text-amber-700';
      case 'rejected': return 'bg-red-100 text-red-700';
      default: return 'bg-slate-100 text-slate-600';
    }
  };

  const getTooltip = (docType) => t.tooltips[docType] || t.defaultTooltip;

  const completedCount = documents.filter(d => d.status === 'verified').length;
  const uploadedCount = documents.filter(d => d.status === 'uploaded').length;
  const totalCount = documents.length;

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="animate-spin text-slate-400" size={48} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-slate-900 text-white p-4">
        <div className="max-w-lg mx-auto flex items-start justify-between">
          <div>
            <h1 className="text-xl font-bold uppercase tracking-tight">{t.docUploadTitle}</h1>
            <p className="text-slate-400 text-sm mt-1">
              {dealInfo.customerName} &bull; {dealInfo.vehicleInfo}
            </p>
            {dealInfo.lender && (
              <p className="text-slate-500 text-xs mt-1">{t.lender}: {dealInfo.lender}</p>
            )}
          </div>
          <LanguageToggle lang={lang} setLang={setLang} />
        </div>
      </div>

      {/* Progress */}
      <div className="bg-white border-b border-slate-200 p-4">
        <div className="max-w-lg mx-auto">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-slate-700">
              {completedCount} {t.of} {totalCount} {t.docsVerified}
            </span>
            <span className="text-sm text-slate-500">
              {uploadedCount} {t.pendingReview}
            </span>
          </div>
          <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-emerald-500 transition-all duration-300"
              style={{ width: `${totalCount > 0 ? (completedCount / totalCount) * 100 : 0}%` }}
            />
          </div>
        </div>
      </div>

      {/* Document List */}
      <div className="max-w-lg mx-auto p-4 space-y-3">
        <TooltipProvider delayDuration={200}>
        {documents.map((doc) => (
          <div
            key={doc.id}
            className={`bg-white border p-4 ${doc.status === 'rejected' ? 'border-red-300' : 'border-slate-200'}`}
            data-testid={`document-${doc.document_type}`}
          >
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-3">
                {getStatusIcon(doc.status)}
                <div>
                  <div className="flex items-center gap-2">
                    <p className="font-medium text-slate-900">{doc.document_type}</p>
                    {doc.is_custom && (
                      <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 font-medium">{t.custom}</span>
                    )}
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button className="text-slate-400 hover:text-slate-600 focus:outline-none">
                          <HelpCircle size={16} />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="top" className="max-w-xs bg-slate-900 text-white text-xs p-3">
                        <p>{getTooltip(doc.document_type)}</p>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                  <span className={`inline-block px-2 py-0.5 text-xs font-semibold mt-1 ${getStatusClass(doc.status)}`}>
                    {getStatusLabel(doc.status)}
                  </span>
                </div>
              </div>
            </div>

            {doc.status === 'rejected' && doc.rejection_reason && (
              <div className="mb-3 p-2 bg-red-50 border border-red-200 text-sm text-red-700">
                <strong>{t.reason}:</strong> {doc.rejection_reason}
              </div>
            )}

            {doc.files && doc.files.length > 0 && (
              <div className="mb-3 p-2 bg-slate-50 border border-slate-200 text-sm">
                <p className="text-slate-500">{lang === 'es' ? 'Subido' : 'Uploaded'}: {doc.files.map(f => f.file_name).join(', ')}</p>
              </div>
            )}

            {(doc.status === 'missing' || doc.status === 'rejected') && (
              <div className="flex gap-2">
                <input
                  type="file"
                  accept=".jpg,.jpeg,.png,.pdf,.heic,.heif"
                  className="hidden"
                  ref={(el) => fileInputRefs[doc.document_type] = el}
                  onChange={(e) => handleFileSelect(doc.document_type, e.target.files[0])}
                />
                <Button
                  size="sm"
                  onClick={() => fileInputRefs[doc.document_type]?.click()}
                  disabled={uploading === doc.document_type}
                  className="flex-1"
                  data-testid={`upload-btn-${doc.document_type}`}
                >
                  {uploading === doc.document_type ? (
                    <><Loader2 size={14} className="mr-1 animate-spin" /> {t.uploading}</>
                  ) : (
                    <><Upload size={14} className="mr-1" /> {t.uploadBtn}</>
                  )}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    const input = document.createElement('input');
                    input.type = 'file';
                    input.accept = 'image/*';
                    input.capture = 'environment';
                    input.onchange = (e) => handleFileSelect(doc.document_type, e.target.files[0]);
                    input.click();
                  }}
                  disabled={uploading === doc.document_type}
                >
                  <Camera size={14} />
                </Button>
              </div>
            )}

            {doc.status === 'uploaded' && (
              <div className="flex items-center gap-2 text-amber-600 text-sm">
                <Clock size={14} />
                {t.waitingReview}
              </div>
            )}

            {doc.status === 'verified' && (
              <div className="flex items-center gap-2 text-emerald-600 text-sm">
                <Check size={14} />
                {t.docAccepted}
              </div>
            )}
          </div>
        ))}
        </TooltipProvider>
      </div>

      {/* Footer */}
      <div className="max-w-lg mx-auto p-4 text-center text-sm text-slate-400">
        <p>{t.sessionExpires} {sessionExpires ? new Date(sessionExpires).toLocaleTimeString() : '30 min'}</p>
        <p className="mt-1">{t.contactDealer}</p>
      </div>
    </div>
  );
};

// Main Component
export const CustomerUploadPage = () => {
  const [step, setStep] = useState('phone');
  const [phone, setPhone] = useState('');
  const [dealId, setDealId] = useState('');
  const [sessionToken, setSessionToken] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [lang, setLang] = useState(() => sessionStorage.getItem('dw_lang') || 'en');

  const handleLangChange = (newLang) => {
    setLang(newLang);
    sessionStorage.setItem('dw_lang', newLang);
  };

  const handlePhoneSuccess = (phoneNumber, selectedDealId) => {
    setPhone(phoneNumber);
    setDealId(selectedDealId);
    setStep('pin');
  };

  const handlePinSuccess = (token, dId, name) => {
    setSessionToken(token);
    setDealId(dId);
    setCustomerName(name);
    setStep('upload');
  };

  const handleBack = () => {
    setStep('phone');
    setPhone('');
    setDealId('');
  };

  if (step === 'phone') {
    return <PhoneEntryScreen onSuccess={handlePhoneSuccess} lang={lang} setLang={handleLangChange} />;
  }

  if (step === 'pin') {
    return (
      <PinVerificationScreen
        phone={phone}
        dealId={dealId}
        onSuccess={handlePinSuccess}
        onBack={handleBack}
        lang={lang}
        setLang={handleLangChange}
      />
    );
  }

  return (
    <DocumentUploadDashboard
      sessionToken={sessionToken}
      dealId={dealId}
      customerName={customerName}
      lang={lang}
      setLang={handleLangChange}
    />
  );
};
